import { theme } from "@/common/Mixin"
import { useTranslation } from "next-i18next"
import styled from "styled-components"
import CheckIcon from "@/common/assets/image/Check.svg"
import NextImg from "../NextImg"
import { ComponentPropsWithoutRef } from "react"

export interface RadioButtonGroupOption {
  label: string
  value: string
  description?: string
}

export interface RadioButtonGroupProps
  extends Omit<ComponentPropsWithoutRef<"input">, "onChange"> {
  options: RadioButtonGroupOption[]
  register: any
  value: string
  onChange: (value: string) => void
  name: string
}

export default function RadioButtonGroup({
  options,
  register,
  name,
  value,
  onChange,
  ...props
}: RadioButtonGroupProps) {
  const { t } = useTranslation(["contactUs"])

  return (
    <Wrapper>
      {options.map(option => (
        <RadioButton
          className={value === option.value ? "selected" : ""}
          key={option.value}
        >
          {value === option.value && (
            <SelectedTag className="button-m">
              <NextImg
                style={{ width: "12px" }}
                src={CheckIcon.src}
                alt="Check Icon"
              />
              {t("radioButtonGroup.selected")}
            </SelectedTag>
          )}
          <input
            onClick={() => {
              onChange(option.value)
            }}
            type="radio"
            value={option.value}
            {...register(name)}
            {...props}
          />
          {<LabelWrapper className="body-bold">{option.label}</LabelWrapper>}
          {option.description && (
            <DescWrapper className="small">{option.description}</DescWrapper>
          )}
        </RadioButton>
      ))}
    </Wrapper>
  )
}

const SelectedTag = styled.span`
  display: flex;
  padding: 8px 12px 8px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  position: absolute;
  top: -20px;
  right: 20px;

  background-color: ${theme.color.primary};
  color: ${theme.color.textGray};
  z-index: 1;
`

const Wrapper = styled.div`
  display: flex;
  /* align-items: flex-start; */
  width: 100%;
  gap: 19px;
  align-self: stretch;
`

const RadioButton = styled.label`
  position: relative;
  display: inline-block;
  padding: 12px 24px;
  gap: 4px;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  border: 2px solid transparent;
  transition:
    background-color 0.3s,
    color 0.3s;

  &.selected {
    border: 2px solid ${theme.color.primary};
  }

  input[type="radio"] {
    display: none;
  }
`

const LabelWrapper = styled.span`
  color: ${theme.color.textGray};
`

const DescWrapper = styled.div`
  color: ${theme.color.secondary};
`
